import React from 'react';
import styled from 'styled-components';
import { Search as SearchIcon, RotateLeft as ResetIcon } from '@mui/icons-material';

export type LessonFilter = 'all' | 'booked' | 'available';

export type TeacherOption = { id: string; name: string };
export type StudentOption = { id: string; name: string };

const FilterBar = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: nowrap;
  align-items: flex-end;
  background: #ffffff;
  padding: 1.5rem 2rem;
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  width: 100%;
  overflow-x: auto;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    gap: 0.75rem;
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    padding: 1.25rem;
    overflow-x: visible;
    border-radius: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: 140px;

  @media (max-width: 768px) {
    flex-shrink: 1;
    min-width: 0;
  }
`;

const Label = styled.span`
  font-size: 0.7rem;
  font-weight: 800;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: 0.25rem;
`;

const baseInputStyles = `
  padding: 0.6rem 1rem;
  border-radius: 12px;
  border: 1.5px solid #f1f5f9;
  background: #fff;
  font-weight: 600;
  color: #475569;
  transition: all 0.2s;
  height: 46px;
  font-size: 0.9rem;

  &:hover {
    border-color: #e2e8f0;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
  }
`;

const Select = styled.select`
  ${baseInputStyles}
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
  padding-right: 2.5rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Input = styled.input`
  ${baseInputStyles}
  cursor: pointer;
  width: 100%;

  &[type="date"] {
    color: #475569;
    font-family: inherit;
  }

  &[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0 1.5rem;
  border-radius: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;
  height: 46px;
  font-size: 0.95rem;

  ${({ $variant }) =>
    $variant === 'secondary'
      ? `
    background: #f1f5f9;
    color: #64748b;
    width: 46px;
    padding: 0;
    border-radius: 14px;
    &:hover { background: #e2e8f0; color: #1e293b; }
  `
      : `
    background: #6366f1;
    color: white;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
    &:hover { background: #4f46e5; transform: translateY(-1px); box-shadow: 0 6px 16px rgba(99, 102, 241, 0.25); }
    &:active { transform: translateY(0); }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-left: auto;
  align-items: flex-end;
  flex-shrink: 0;

  @media (max-width: 768px) {
    grid-column: span 2;
    margin-left: 0;
    width: 100%;
    gap: 1rem;
    margin-top: 0.5rem;

    button {
      flex: 1;
    }
  }
`;

export type LessonListFiltersProps = {
  // controlled values
  lessonFilter: LessonFilter;
  teacherId?: string; // 'all' or teacher id
  studentId?: string; // 'all' or student id
  selectedDate: string; // yyyy-MM-dd or ''
  startDate?: string;
  endDate?: string;
  lessonType?: string;
  licenseType?: string;
  // options
  teacherOptions?: TeacherOption[];
  studentOptions?: StudentOption[];
  // handlers
  onLessonFilterChange: (next: LessonFilter) => void;
  onTeacherChange?: (next: string) => void;
  onStudentChange?: (next: string) => void;
  onDateChange: (next: string) => void;
  onStartDateChange?: (next: string) => void;
  onEndDateChange?: (next: string) => void;
  onLessonTypeChange?: (next: string) => void;
  onLicenseTypeChange?: (next: string) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  // optional aria and class
  className?: string;
  style?: React.CSSProperties;
  allowedStatuses?: LessonFilter[];
};

/**
 * Modern centralized filter controls for lesson lists.
 */
const LessonListFilters: React.FC<LessonListFiltersProps> = ({
  lessonFilter,
  teacherId,
  studentId,
  selectedDate,
  startDate,
  endDate,
  lessonType,
  licenseType,
  teacherOptions = [],
  studentOptions = [],
  onLessonFilterChange,
  onTeacherChange,
  onStudentChange,
  onDateChange,
  onStartDateChange,
  onEndDateChange,
  onLessonTypeChange,
  onLicenseTypeChange,
  onApplyFilters,
  onResetFilters,
  className,
  style,
  allowedStatuses,
}) => {
  const renderStatusOptions = () => {
    const allOptions = [
      { value: 'all', label: 'All' },
      { value: 'available', label: 'Available' },
    ];

    const filteredOptions = allowedStatuses 
      ? allOptions.filter(opt => allowedStatuses.includes(opt.value as LessonFilter))
      : allOptions;

    return filteredOptions.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ));
  };

  return (
    <FilterBar className={className} style={style}>
      {onStudentChange && (
        <FormGroup>
          <Label>Student</Label>
          <Select value={studentId || 'all'} onChange={(e) => onStudentChange(e.target.value)}>
            <option value="all">All students</option>
            {studentOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name || `Student ${s.id}`}</option>
            ))}
          </Select>
        </FormGroup>
      )}

      <FormGroup>
        <Label>Day</Label>
        <Input 
          role="textbox" 
          aria-label="Filter by date" 
          type="date" 
          value={selectedDate} 
          onChange={(e) => onDateChange(e.target.value)} 
          placeholder="Select day"
        />
      </FormGroup>

      <FormGroup>
        <Label>From</Label>
        <Input 
          type="date" 
          value={startDate || ''} 
          onChange={(e) => onStartDateChange?.(e.target.value)} 
          placeholder="Start date"
        />
      </FormGroup>

      <FormGroup>
        <Label>To</Label>
        <Input 
          type="date" 
          value={endDate || ''} 
          onChange={(e) => onEndDateChange?.(e.target.value)} 
          placeholder="End date"
        />
      </FormGroup>

      <FormGroup>
        <Label>Type</Label>
        <Select value={lessonType || ''} onChange={(e) => onLessonTypeChange?.(e.target.value)}>
          <option value="">All Types</option>
          <option value="REGULAR">Regular</option>
          <option value="EXAM">Exam</option>
          <option value="TRIAL">Trial</option>
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>License Type</Label>
        <Select value={licenseType || ''} onChange={(e) => onLicenseTypeChange?.(e.target.value)}>
          <option value="">All Licenses</option>
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="BE">BE</option>
          <option value="C">C</option>
          <option value="D">D</option>
          <option value="AM">AM</option>
        </Select>
      </FormGroup>

      <ButtonGroup>
        <ActionButton $variant="secondary" onClick={onResetFilters} title="Reset all filters">
          <ResetIcon sx={{ fontSize: 18 }} />
        </ActionButton>
        <ActionButton onClick={onApplyFilters}>
          <SearchIcon sx={{ fontSize: 18 }} />
          Filter
        </ActionButton>
      </ButtonGroup>
    </FilterBar>
  );
};

export default LessonListFilters;
